import { Helmet } from "react-helmet-async";
import { Link } from "wouter";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useLogin } from "@workspace/api-client-react";
import { useToast } from "@/hooks/use-toast";
import { Loader2 } from "lucide-react";
import { AuthShell, glassInput, glassLabel, glassLabelBase, glassError, glassCta, glassLink } from "@/components/auth/AuthShell";

const loginSchema = z.object({
  email:    z.string().email("Please enter a valid email"),
  password: z.string().min(1, "Password is required"),
});

export default function Login() {
  const { toast } = useToast();
  const loginMutation = useLogin();

  const form = useForm<z.infer<typeof loginSchema>>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: "", password: "" },
  });

  const onSubmit = (data: z.infer<typeof loginSchema>) => {
    loginMutation.mutate({ data }, {
      onSuccess: (res) => {
        localStorage.setItem("commonqr_token", res.token);
        window.location.href = "/dashboard";
      },
      onError: (error: any) => {
        toast({
          title: "Login Failed",
          description: error?.message || "Invalid credentials. Please try again.",
          variant: "destructive",
        });
      },
    });
  };

  const handleGoogleLogin = () => {
    window.location.href = "/api/v1/auth/google";
  };

  return (
    <AuthShell
      title="Welcome back"
      subtitle="Sign in to manage your QR profile"
      dividerText="or sign in with email"
      onGoogle={handleGoogleLogin}
      footer={
        <>
          Don't have an account?{" "}
          <Link href="/signup" className={`font-semibold ${glassLink}`}>Create one</Link>
        </>
      }
    >
      <Helmet>
        <title>Log In — CommonQR</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
        <div>
          <label htmlFor="login-email" className={glassLabel}>Email Address</label>
          <input
            id="login-email"
            type="email"
            placeholder="you@example.com"
            autoComplete="email"
            {...form.register("email")}
            className={glassInput}
          />
          {form.formState.errors.email && (
            <p className={glassError}>{form.formState.errors.email.message}</p>
          )}
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <label htmlFor="login-password" className={glassLabelBase}>Password</label>
            <a href="/forgot-password" className={`text-xs ${glassLink}`}>Forgot password?</a>
          </div>
          <input
            id="login-password"
            type="password"
            placeholder="••••••••"
            autoComplete="current-password"
            {...form.register("password")}
            className={glassInput}
          />
          {form.formState.errors.password && (
            <p className={glassError}>{form.formState.errors.password.message}</p>
          )}
        </div>

        <button type="submit" disabled={loginMutation.isPending} className={glassCta}>
          {loginMutation.isPending ? <Loader2 className="w-5 h-5 animate-spin" /> : "Sign In"}
        </button>
      </form>
    </AuthShell>
  );
}
